import {
  DynamoDBClient,
  ScanCommand,
  DeleteItemCommand,
  PutItemCommand,
  UpdateItemCommand,
} from '@aws-sdk/client-dynamodb';
import {
  marshall,
  unmarshall,
} from '@aws-sdk/util-dynamodb';

import PQueue from 'p-queue/dist/index';
import * as Yup from 'yup';

// local aws
import getAWSConfig from '@/aws/aws-sdk-config';
import awsconfig from '@/aws-exports';

// utils
import generateRandomHash from '@/utils/generateRandomHash';
import arrayChunk from '@/utils/arrayChunk';

// validation
const fileMetadataSchema = Yup.object().shape({
  numPages: Yup.number().integer().optional(),
  size: Yup.number().integer().min(0).optional(),
  lastModified: Yup.number().integer().min(0).optional(),
  uploadedTimestamp: Yup.number().integer().min(0).optional(),
  unionHash: Yup.string().optional(),
  type: Yup.string().optional(),
  tags: Yup.array().of(Yup.string()),
});
// Define the validation schema for the input object
const fileCreateSchema = Yup.object().shape({
  id: Yup.string().required(),
  userID: Yup.string().required(),
  userEmail: Yup.string().email().required(),
  key: Yup.string().required(),
  alias: Yup.string().max(45).required(),
  metadata: fileMetadataSchema,
});
const fileUpdateSchema = Yup.object().shape({
  id: Yup.string().required(),
  alias: Yup.string().max(45).optional(),
  metadata: fileMetadataSchema,
});

const TABLE_NAME_FILE = awsconfig.dynamodb.tables.FILE;
const TABLE_NAME_TAG = awsconfig.dynamodb.tables.TAG;

// Request throttling
// https://github.com/sindresorhus/p-queue
const queue = new PQueue({
  concurrency: 5,
  intervalCap: 5,
  interval: 1000,
});

function queueWrapper(fn) {
  return async (...args) => queue.add(() => fn(...args));
}

/**
 * @async
 *
 * Retrieve all elements in the File database.
 * @returns {Promise<object>} - A promise that resolves to an object containing
 * all files in the database.
 *
 * The object has the following structure:
 * {
 *   Items: Array<object>, // Array of objects representing individual files
 *   Count: number,        // Total number of files returned
 *   ScannedCount: number  // Total number of items scanned
 * }
 */
async function getAllDBFiles() {
  const params = {
    TableName: TABLE_NAME_FILE,
  };

  const dbClient = new DynamoDBClient(getAWSConfig());
  const fileData = await dbClient.send(new ScanCommand(params));

  // Unmarshall the data into appropriate JS types
  return fileData.Items.map(item => unmarshall(item));
}

async function getAllDBTags() {
  const params = {
    TableName: TABLE_NAME_TAG,
  };

  const dbClient = new DynamoDBClient(getAWSConfig());
  const fileData = await dbClient.send(new ScanCommand(params));

  // Unmarshall the data into appropriate JS types
  return fileData.Items.map(item => unmarshall(item));
}

/**
 * @async
 *
 * Retrieve files in the database that match the specified keys.
 * @param {(string|string[])} keys - A string or an array of strings representing
 * the keys of the files to retrieve. If a string is passed, it will be wrapped in an array.
 * @returns {Promise<object>} - A promise that resolves to an object containing the files
 * in the database that match the specified keys.
 * The object has the following structure:
 * [{
 *   alias: string,
 *   createdAt: string,
 *   id: string,
 *   key: string,
 *   metadata: string,
 *   updatedAt: string,
 *   userId: string,
 * }]
 */
async function getDBFiles(keys) {
  const filterKeys = typeof keys === 'string' ? [keys] : keys;
  const dbClient = new DynamoDBClient(getAWSConfig());

  // Split the keys into chunks of 100 or less
  const chunks = arrayChunk(filterKeys, 100);

  // Perform a scan for each chunk
  const dbRequests = chunks.map((chunk) => {
    // Setting up this param attribute to correct format. Example:
    // { ':aBcDe12345': { S: 'key_case_studies/1041224 Mar 2023 15 VApu6.pdf' }, ... },
    const ExpressionAttributeValues = chunk.reduce((acc, curr) => {
      acc[`:${generateRandomHash(10)}`] = { S: curr };
      return acc;
    }, {});

    // Scan docs: https://docs.aws.amazon.com/amazondynamodb/latest/developerguide/example_dynamodb_Scan_section.html
    // Possible properties: https://docs.aws.amazon.com/AWSJavaScriptSDK/latest/AWS/DynamoDB.html#scan-property
    // Syntax: https://docs.aws.amazon.com/amazondynamodb/latest/developerguide/Expressions.OperatorsAndFunctions.html#Expressions.OperatorsAndFunctions.Syntax
    const params = {
      TableName: TABLE_NAME_FILE,
      FilterExpression: `#k IN (${Object.keys(ExpressionAttributeValues).join(', ')})`,
      ExpressionAttributeNames: {
        '#k': 'key',
      },
      ExpressionAttributeValues,
    };

    return dbClient.send(new ScanCommand(params));
  });

  const fulfilledRequests = await Promise.all(dbRequests);

  // Unmarshall the data into appropriate JS types
  return fulfilledRequests
    .flatMap(req => req.Items) // Combine all response items into single array
    .map(item => unmarshall(item)); // Unmarshall the data into appropriate JS types
}

async function getDBFilesByUnionHash(unionHash) {
  const params = {
    TableName: TABLE_NAME_FILE,
    FilterExpression: 'metadata.unionHash = :unionHash',
    ExpressionAttributeValues: {
      ':unionHash': { S: unionHash },
    },
  };

  const dbClient = new DynamoDBClient(getAWSConfig());
  const fileData = await dbClient.send(new ScanCommand(params));

  // Unmarshall the data into appropriate JS types
  return fileData.Items.map(item => unmarshall(item));
}

async function getDBFilesByTags(tagsIn) {
  const tags = Array.isArray(tagsIn) ? tagsIn : [tagsIn];
  // Create a filter expression and attribute values for tags
  const filterExpressionParts = [];
  const expressionAttributeValues = {};

  tags.forEach((tag, index) => {
    filterExpressionParts.push(`contains(metadata.tags, :tag${index})`);
    expressionAttributeValues[`:tag${index}`] = tag;
  });

  const filterExpression = filterExpressionParts.join(' AND ');

  const params = {
    TableName: TABLE_NAME_FILE,
    FilterExpression: filterExpression,
    ExpressionAttributeValues: marshall(expressionAttributeValues),
  };

  const dbClient = new DynamoDBClient(getAWSConfig());
  const fileData = await dbClient.send(new ScanCommand(params));

  // Unmarshall the data into appropriate JS types
  return fileData.Items.map(item => unmarshall(item));
}

async function getDBFilesByKeyPrefix(prefix) {
  const params = {
    TableName: TABLE_NAME_FILE,
    ExpressionAttributeNames: {
      '#k': 'key',
    },
    ExpressionAttributeValues: {
      ':prefix': { S: prefix },
    },
    FilterExpression: 'begins_with(#k, :prefix)',
  };

  const dbClient = new DynamoDBClient(getAWSConfig());
  const fileData = await dbClient.send(new ScanCommand(params));

  // Unmarshall the data into appropriate JS types
  return fileData.Items.map(item => unmarshall(item));
}


async function getDBTagsByTopic(topic) {
  const params = {
    TableName: TABLE_NAME_TAG,
    FilterExpression: 'topic = :topic',
    ExpressionAttributeValues: {
      ':topic': { S: topic },
    },
  };

  const dbClient = new DynamoDBClient(getAWSConfig());
  const fileData = await dbClient.send(new ScanCommand(params));

  // Unmarshall the data into appropriate JS types
  return fileData.Items.map(item => unmarshall(item));
}

async function deleteDBFile(key) {
  const params = {
    TableName: TABLE_NAME_FILE,
    Key: { id: { S: key } },
  };

  const command = new DeleteItemCommand(params);
  const dbClient = new DynamoDBClient(getAWSConfig());
  const result = await dbClient.send(command);

  return result;
}
async function deleteDBTag(tagId) {
  const params = {
    TableName: TABLE_NAME_TAG,
    Key: { id: { S: tagId } },
  };

  const command = new DeleteItemCommand(params);
  const dbClient = new DynamoDBClient(getAWSConfig());
  const result = await dbClient.send(command);

  return result;
}

async function updateDBFile(input) {
  // Validate the input (throws an error if invalid)
  await fileUpdateSchema.validate(input);

  // Generate the update expression and attribute values
  let updateExpression = 'SET';
  const expressionAttributeValues = {};

  Object.entries(input).forEach(([key, value]) => {
    if (key !== 'id') {
      updateExpression += ` #${key} = :${key},`;
      expressionAttributeValues[`:${key}`] = value;
    }
  });

  // Remove the trailing comma
  updateExpression = updateExpression.slice(0, -1);

  const params = {
    TableName: TABLE_NAME_FILE,
    Key: marshall({ id: input.id }),
    UpdateExpression: updateExpression,
    ExpressionAttributeNames: Object.keys(input).reduce((acc, key) => {
      if (key !== 'id') {
        acc[`#${key}`] = key;
      }
      return acc;
    }, {}),
    ExpressionAttributeValues: marshall(expressionAttributeValues),
  };

  const dbClient = new DynamoDBClient(getAWSConfig());
  const result = await dbClient.send(new UpdateItemCommand(params));

  return result;
}

async function updateDBTag(key, name) {
  const params = {
    TableName: TABLE_NAME_TAG,
    Key: marshall({ id: key }),
    UpdateExpression: 'SET #a = :a',
    ExpressionAttributeNames: {
      '#a': 'name',
    },
    ExpressionAttributeValues: {
      ':a': { S: name },
    },
  };

  const dbClient = new DynamoDBClient(getAWSConfig());
  const result = await dbClient.send(new UpdateItemCommand(params));

  return result;
}

async function createDBFile(input) {
  // Validate the input (throws an error if invalid)
  await fileCreateSchema.validate(input);

  const params = {
    TableName: TABLE_NAME_FILE,
    Item: marshall(input),
  };

  const dbClient = new DynamoDBClient(getAWSConfig());
  const result = await dbClient.send(new PutItemCommand(params));

  return result;
}

async function createDBTag(input) {
  const params = {
    TableName: TABLE_NAME_TAG,
    Item: marshall(input),
    ConditionExpression: 'attribute_not_exists(id)',
  };

  const dbClient = new DynamoDBClient(getAWSConfig());
  const result = await dbClient.send(new PutItemCommand(params));

  return result;
}


// This proxy solution is an attempte to throttle all requests to the database
// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Proxy
//
// In essence we capture all calls to functions that interact
// directly with the database (as low down as possible) and wrap them
// in a queue (a Thottling library)
// This is the best example demonstration of throttling I've found so far:
// https://jsfiddle.net/missinglink/19e2r2we/
//
// The configuration for the throttling library is set in the queue variable above.
// If it is required to understand this configuration of expand it, please see:
// https://github.com/sindresorhus/p-queue
//
// The reason why this is necessary is that DynamoDB has request limit, and there is a
// chance that we will exceed it if we don't throttle our requests.
// https://docs.aws.amazon.com/amazondynamodb/latest/developerguide/Limits.html
//
// Worth noting that this is not a perfect solution, as is build with the assumption
// that:
// 1. All database requests are made through this file
// 2. There will not be heavy concurrent traffic to the site
//
// If these measures turns out to not be enough I would recommend to look into
// batch requests, which is also a feature of DynamoDB.
const queProxy = new Proxy(
  {
    // Files
    getDBFiles,
    getDBFilesByUnionHash,
    getDBFilesByTags,
    getDBFilesByKeyPrefix,
    getAllDBFiles,
    deleteDBFile,
    createDBFile,
    updateDBFile,
    // Tags
    getAllDBTags,
    createDBTag,
    getDBTagsByTopic,
    deleteDBTag,
    updateDBTag,
  },
  {
    get(target, key) {
      if (typeof target[key] === 'function') {
        return queueWrapper(target[key]);
      }
      return target[key];
    },
  },
);

export default queProxy;
